import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Search, QrCode, Link as LinkIcon, ExternalLink, LogOut, Upload, Building2, Image as ImageIcon, Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { clientApi, uploadApi, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import QRDialog from "@/components/QRDialog";

export default function ClientDashboard() {
  const nav = useNavigate();
  const { user, logout, setUser } = useAuth();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [qrCard, setQrCard] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setCards(await clientApi.listCards());
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return cards;
    return cards.filter((c) =>
      [c.full_name, c.email, c.slug, c.company, c.job_title]
        .filter(Boolean).some((f) => f.toLowerCase().includes(q))
    );
  }, [cards, search]);

  const publicUrl = (slug) => `${window.location.origin}/card/${slug}`;
  const copyLink = async (slug) => {
    await navigator.clipboard.writeText(publicUrl(slug));
    toast.success("Public link copied");
  };

  const doLogout = async () => {
    await logout();
    nav("/login");
  };

  const active = cards.filter((c) => c.active).length;

  return (
    <div className="min-h-screen bg-[#07111D] text-white">
      <header className="h-16 border-b border-[#1E2D40] flex items-center justify-between px-6 md:px-8 bg-[#07111D]/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#0B131E] border border-[#1E2D40] flex items-center justify-center overflow-hidden">
            {user?.company_logo_url ? (
              <img src={user.company_logo_url} alt="" className="w-full h-full object-cover" data-testid="client-logo" />
            ) : (
              <span className="text-[#18BFFF] font-bold text-sm">
                {(user?.name || user?.email || "?").slice(0, 2).toUpperCase()}
              </span>
            )}
          </div>
          <div>
            <h1 className="font-display text-lg font-semibold leading-tight" data-testid="client-dashboard-title">
              {user?.name || "Client"} dashboard
            </h1>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#4A5A73] leading-tight">{user?.email}</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={doLogout} data-testid="logout-btn"
                className="border-[#1E2D40] bg-transparent text-white hover:bg-[#162436] hover:text-white">
          <LogOut className="w-4 h-4 mr-2" />
          Sign out
        </Button>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-8 py-8">
        <Tabs defaultValue="cards">
          <TabsList className="bg-[#0B131E] border border-[#1E2D40]">
            <TabsTrigger value="cards" data-testid="tab-cards">My cards</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="cards" className="mt-6 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <StatCard label="Assigned to you" value={cards.length} testid="stat-total" />
              <StatCard label="Active" value={active} testid="stat-active" />
              <StatCard label="Draft" value={cards.length - active} testid="stat-draft" />
            </div>

            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5A73]" />
              <Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)}
                     data-testid="search-input"
                     className="pl-9 bg-[#0B131E] border-[#1E2D40] text-white placeholder:text-[#4A5A73] focus-visible:ring-[#18BFFF] focus-visible:border-[#18BFFF]" />
            </div>

            <div className="rounded-xl border border-[#1E2D40] overflow-hidden bg-[#101B28]">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#1E2D40] hover:bg-transparent">
                    <TableHead className="text-[#94A3B8] font-medium">Name</TableHead>
                    <TableHead className="text-[#94A3B8] font-medium hidden md:table-cell">Job</TableHead>
                    <TableHead className="text-[#94A3B8] font-medium">Slug</TableHead>
                    <TableHead className="text-[#94A3B8] font-medium">Status</TableHead>
                    <TableHead className="text-right text-[#94A3B8] font-medium">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && (
                    <TableRow className="border-[#1E2D40]">
                      <TableCell colSpan={5} className="text-center py-10 text-[#94A3B8]">
                        <Loader2 className="w-5 h-5 inline animate-spin text-[#18BFFF]" />
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading && filtered.length === 0 && (
                    <TableRow className="border-[#1E2D40]">
                      <TableCell colSpan={5} className="text-center py-10 text-[#94A3B8]" data-testid="empty-state">
                        No cards assigned yet.
                      </TableCell>
                    </TableRow>
                  )}
                  {filtered.map((c) => (
                    <TableRow key={c.id} className="border-[#1E2D40] hover:bg-[#162436]" data-testid={`row-${c.slug}`}>
                      <TableCell className="font-medium text-white">
                        {c.full_name || <span className="text-[#4A5A73]">Unnamed</span>}
                      </TableCell>
                      <TableCell className="text-[#94A3B8] hidden md:table-cell">{c.job_title || "—"}</TableCell>
                      <TableCell>
                        <code className="text-xs bg-[#0B131E] border border-[#1E2D40] rounded px-2 py-0.5 text-[#18BFFF]">{c.slug}</code>
                      </TableCell>
                      <TableCell>
                        <Badge className={c.active
                          ? "bg-[#18BFFF]/15 text-[#18BFFF] border border-[#18BFFF]/30 hover:bg-[#18BFFF]/15"
                          : "bg-[#162436] text-[#94A3B8] border border-[#1E2D40] hover:bg-[#162436]"}>
                          {c.active ? "Active" : "Draft"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center gap-1 justify-end">
                          <IconBtn title="QR" onClick={() => setQrCard(c)} testid={`qr-${c.slug}`}><QrCode className="w-4 h-4" /></IconBtn>
                          <IconBtn title="Copy" onClick={() => copyLink(c.slug)} testid={`copy-${c.slug}`}><LinkIcon className="w-4 h-4" /></IconBtn>
                          <IconBtn title="Open" onClick={() => window.open(publicUrl(c.slug), "_blank")} testid={`open-${c.slug}`}><ExternalLink className="w-4 h-4" /></IconBtn>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <p className="text-xs text-[#4A5A73]" data-testid="count-label">
              Showing {filtered.length} of {cards.length} cards
            </p>
          </TabsContent>

          <TabsContent value="settings" className="mt-6">
            <SettingsPanel user={user} onUpdated={(u) => setUser(u)} />
          </TabsContent>
        </Tabs>
      </div>

      {qrCard && <QRDialog card={qrCard} open={!!qrCard} onOpenChange={(v) => !v && setQrCard(null)} />}
    </div>
  );
}

function SettingsPanel({ user, onUpdated }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [saving, setSaving] = useState(false);

  const chooseFile = () => fileRef.current?.click();

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const { url } = await uploadApi.logo(f);
      const updated = await clientApi.updateSettings({ company_logo_url: url });
      onUpdated?.(updated);
      toast.success("Logo updated");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const saveName = async () => {
    setSaving(true);
    try {
      const updated = await clientApi.updateSettings({ name });
      onUpdated?.(updated);
      toast.success("Profile saved");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally { setSaving(false); }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="client-settings">
      <div className="rounded-2xl border border-[#1E2D40] bg-[#101B28] p-6">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#4A5A73]">
          <ImageIcon className="w-3 h-3" /> Company logo
        </div>
        <div className="mt-4 flex items-center gap-5">
          <div className="w-24 h-24 rounded-2xl border border-[#1E2D40] bg-[#0B131E] flex items-center justify-center overflow-hidden">
            {user?.company_logo_url ? (
              <img src={user.company_logo_url} alt="Logo" className="w-full h-full object-cover" data-testid="logo-preview" />
            ) : (
              <ImageIcon className="w-8 h-8 text-[#4A5A73]" />
            )}
          </div>
          <div className="flex-1">
            <Button
              type="button"
              onClick={chooseFile}
              disabled={uploading}
              data-testid="upload-logo-btn"
              className="bg-[#18BFFF] text-[#07111D] hover:bg-[#4DD0FF]"
            >
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {uploading ? "Uploading..." : "Upload logo"}
            </Button>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                   onChange={handleFile} className="hidden" data-testid="logo-file-input" />
            <p className="mt-2 text-xs text-[#4A5A73]">PNG, JPG or WebP. Max 4MB. Appears on your dashboard and cards.</p>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-[#1E2D40] bg-[#101B28] p-6">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#4A5A73]">
          <Building2 className="w-3 h-3" /> Company / display name
        </div>
        <div className="mt-4 space-y-3">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Elite Digital HQ"
                 data-testid="input-display-name"
                 className="bg-[#0B131E] border-[#1E2D40] text-white placeholder:text-[#4A5A73] focus-visible:ring-[#18BFFF] focus-visible:border-[#18BFFF] h-11" />
          <Button onClick={saveName} disabled={saving} data-testid="save-name-btn"
                  className="bg-[#18BFFF] text-[#07111D] hover:bg-[#4DD0FF]">
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
        <div className="mt-6 border-t border-[#1E2D40] pt-4 text-xs text-[#94A3B8]">
          <div className="flex justify-between"><span>Email</span><span className="text-white">{user?.email}</span></div>
          <div className="flex justify-between mt-1"><span>Role</span><span className="text-white">{user?.role}</span></div>
          <div className="flex justify-between mt-1"><span>User ID</span><span className="text-white truncate ml-2">{user?.user_id}</span></div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <div className="rounded-xl border border-[#1E2D40] bg-[#101B28] p-5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#4A5A73]">{label}</div>
      <div className="mt-1 font-display text-3xl font-semibold text-white">{value}</div>
    </div>
  );
}

function IconBtn({ children, onClick, title, testid }) {
  return (
    <button
      onClick={onClick}
      title={title}
      data-testid={testid}
      className="w-8 h-8 rounded-md flex items-center justify-center transition-colors text-[#94A3B8] hover:bg-[#162436] hover:text-[#18BFFF]"
    >
      {children}
    </button>
  );
}
